import { useMemo } from "react";
import moment from "moment-timezone";
import type { DateRangeValue } from "../DateRangePicker";
import { CalendarIcon } from "./CalendarIcon";
import "./styles.css";

export interface DateRangeButtonProps {
  value?: DateRangeValue;
  onClick: () => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}

/**
 * Botão para abrir o seletor de período de datas.
 * Exibe o período selecionado ou um placeholder quando não há seleção.
 *
 * @param value - Período de datas selecionado
 * @param onClick - Callback ao clicar no botão
 * @param disabled - Se o botão está desabilitado
 * @param placeholder - Texto exibido quando não há seleção (padrão: "Data")
 * @param className - Classes CSS adicionais
 */
export function DateRangeButton({
  value,
  onClick,
  disabled = false,
  placeholder = "Data",
  className = "",
}: DateRangeButtonProps) {
  const displayText = useMemo(() => {
    if (!value?.startDate || !value?.endDate) return placeholder;

    const start = moment(value.startDate).format("DD/MM/YYYY");
    const end = moment(value.endDate).format("DD/MM/YYYY");

    // Se for o mesmo dia, mostrar apenas uma data
    if (start === end) return start;

    return `${start} - ${end}`;
  }, [value, placeholder]);

  const hasValue = value?.startDate && value?.endDate;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`date-range-button ${hasValue ? "has-value" : ""} ${className}`}
    >
      <span className="date-range-button-text">{displayText}</span>
      <CalendarIcon className="date-range-button-icon" />
    </button>
  );
}
